import {
  Component,
  Output,
  EventEmitter,
  signal,
  effect,
  Input,
} from '@angular/core'
import { PrimeNGConfig } from 'primeng/api'
import { TableFilters } from '../grid/table.component'
import { TranslationService } from '@evologi/shared/util-translation'

interface DateFilterOperator {
  label: string
  value: string
  icon: string
  symbol: string
}
@Component({
  selector: 'op-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
})
export class CalendarComponent {
  @Input({ required: true })
  field!: string
  @Input() tableFilters?: TableFilters
  @Input() clearButton = false

  // Emitters
  @Output() readonly filtersSelect = new EventEmitter<TableFilters>()
  @Output() readonly operatorSelect = new EventEmitter<string>()

  public selectedDate = signal<Date | Date[] | undefined>(undefined)

  // Date filter operators
  public dateFilterOperators: Partial<Record<string, DateFilterOperator>> = {}
  public dateFilterOperatorOptions: DateFilterOperator[] = [
    {
      label: this.translationService.translate('Equals'),
      value: '=',
      icon: '=',
      symbol: '=',
    },
    {
      label: this.translationService.translate('Less than'),
      value: '<',
      icon: '<',
      symbol: '<',
    },
    {
      label: this.translationService.translate('Greater than'),
      value: '>',
      icon: '>',
      symbol: '>',
    },
    {
      label: this.translationService.translate('Less or equal than'),
      value: '<=',
      icon: '≤',
      symbol: '≤',
    },
    {
      label: this.translationService.translate('Greater or equal than'),
      value: '>=',
      icon: '≥',
      symbol: '≥',
    },
    {
      label: this.translationService.translate('Between'),
      value: 'between',
      icon: '↔',
      symbol: '↔',
    },
  ]
  public dateSelectedField: string | undefined

  constructor(
    private primeNGConfig: PrimeNGConfig,
    private translationService: TranslationService,
  ) {
    this.setTranslation()
    effect(() => {
      const date = this.selectedDate()
      if (
        this.dateSelectedField &&
        ((Array.isArray(date) && date.length === 2) || date)
      ) {
        this.onDateInput(date, this.dateSelectedField)
      }
    })
  }

  onDateInput(date: Date | Date[] | null, field: string) {
    if (
      Array.isArray(date) &&
      date.filter((d) => d !== null && d !== undefined).length !== 2
    ) {
      return
    }
    const operator: DateFilterOperator =
      this.dateFilterOperators[field] || this.dateFilterOperatorOptions[0]

    // Handle different operators
    let filterValue: Date | Date[] | undefined = Array.isArray(date)
      ? date[0]
      : (date ?? undefined)
    let filterKey = field
    let primeOperator = '='

    switch (operator.value) {
      case '=':
        primeOperator = 'equals'
        filterKey = field
        break
      case '<':
        primeOperator = 'lt'
        filterKey = `${field}:lt`
        break
      case '>':
        primeOperator = 'gt'
        filterKey = `${field}:gt`
        break
      case '<=':
        primeOperator = 'lte'
        filterKey = `${field}:le`
        break
      case '>=':
        primeOperator = 'gte'
        filterKey = `${field}:ge`
        break
      case 'between':
        filterValue = !date
          ? undefined
          : Array.isArray(date)
            ? date
            : [date, date]
        primeOperator = 'between'
        filterKey = `${field}:between`
        break
      default:
        primeOperator = 'equals'
        filterKey = field
    }
    this.operatorSelect.emit(primeOperator)
    this.filtersSelect.emit({
      [filterKey]: filterValue || undefined,
    })
  }

  resetDate() {
    this.selectedDate.set(undefined)
  }

  onDateOperatorChange(operator: DateFilterOperator, field: string) {
    this.dateFilterOperators[field] = operator
    this.resetDate()
  }

  getDateFilterSelectionMode(field: string): 'single' | 'range' {
    const operator =
      this.dateFilterOperators[field] ?? this.dateFilterOperatorOptions[0]
    return operator.value === 'between' ? 'range' : 'single'
  }

  setTranslation() {
    // Configure PrimeNG with Italian locale
    this.primeNGConfig.setTranslation({
      firstDayOfWeek: 1,
      dayNames: [
        this.translationService.translate('Sunday'),
        this.translationService.translate('Monday'),
        this.translationService.translate('Tuesday'),
        this.translationService.translate('Wednesday'),
        this.translationService.translate('Thursday'),
        this.translationService.translate('Friday'),
        this.translationService.translate('Saturday'),
      ],
      dayNamesShort: [
        this.translationService.translate('Sunday').slice(0, 3),
        this.translationService.translate('Monday').slice(0, 3),
        this.translationService.translate('Tuesday').slice(0, 3),
        this.translationService.translate('Wednesday').slice(0, 3),
        this.translationService.translate('Thursday').slice(0, 3),
        this.translationService.translate('Friday').slice(0, 3),
        this.translationService.translate('Saturday').slice(0, 3),
      ],
      dayNamesMin: [
        this.translationService.translate('Sunday').slice(0, 1),
        this.translationService.translate('Monday').slice(0, 1),
        this.translationService.translate('Tuesday').slice(0, 1),
        this.translationService.translate('Wednesday').slice(0, 1),
        this.translationService.translate('Thursday').slice(0, 1),
        this.translationService.translate('Friday').slice(0, 1),
        this.translationService.translate('Saturday').slice(0, 1),
      ],
      monthNames: [
        this.translationService.translate('January'),
        this.translationService.translate('February'),
        this.translationService.translate('March'),
        this.translationService.translate('April'),
        this.translationService.translate('May'),
        this.translationService.translate('June'),
        this.translationService.translate('July'),
        this.translationService.translate('August'),
        this.translationService.translate('September'),
        this.translationService.translate('October'),
        this.translationService.translate('November'),
        this.translationService.translate('December'),
      ],
      monthNamesShort: [
        this.translationService.translate('January').slice(0, 3),
        this.translationService.translate('February').slice(0, 3),
        this.translationService.translate('March').slice(0, 3),
        this.translationService.translate('April').slice(0, 3),
        this.translationService.translate('May').slice(0, 3),
        this.translationService.translate('June').slice(0, 3),
        this.translationService.translate('July').slice(0, 3),
        this.translationService.translate('August').slice(0, 3),
        this.translationService.translate('September').slice(0, 3),
        this.translationService.translate('October').slice(0, 3),
        this.translationService.translate('November').slice(0, 3),
        this.translationService.translate('December').slice(0, 3),
      ],
      today: this.translationService.translate('Today'),
      clear: this.translationService.translate('Delete'),
    })
  }

  get selectedDateRange(): [Date | null, Date | null] | null {
    const d = this.selectedDate()
    if (Array.isArray(d) && d.length === 2) return [d[0], d[1]]
    return null
  }

  formatShortDate(date: Date | null): string {
    if (!date) return '…'
    return `${date.getDate()}/${date.getMonth() + 1}/${String(date.getFullYear()).slice(-2)}`
  }
}
